import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Button, Divider, Input, Space, Tag, Typography } from 'antd';
import { CloseOutlined, EditOutlined, PlusOutlined, ProfileTwoTone, SearchOutlined } from '@ant-design/icons';
import { AppDispatch } from 'src/index';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import tileStyles from 'src/styles/tile.module.css';
import { Link, useParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { useCollections } from 'src/features/collections/collections.hooks';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { DragEndEvent } from '@dnd-kit/core/dist/types';
import { CSS } from '@dnd-kit/utilities';
import { Collection } from 'src/features/collections/collections.types';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';
import {
  addCollectionsToTemplate,
  removeCollectionsFromTemplate,
  updateTemplateCollectionsOrder,
} from 'src/features/templates/templates.state';
import { useTemplateById, useTemplateCollectionsSectionById } from 'src/features/templates/templates.hooks';
import { ContentTemplate } from 'src/features/templates/templates.types';
import { fetchCollections } from 'src/features/collections/collections.state';

interface TemplateAddCollectionsProps {
  templateId: string;
  currentCollections: Collection[];
}

const TemplateAddCollections: React.FC<TemplateAddCollectionsProps> = props => {
  const intl = useIntl();
  const dispatch: AppDispatch = useDispatch();
  const collections = useCollections();
  const [name, setName] = useState<string | undefined>(undefined);
  const currentCollectionIds = useMemo(() => props.currentCollections.map(c => c.id), [props.currentCollections]);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddButtonClick = useCallback(
    async (collectionId: string) => {
      setIsLoading(true);
      await dispatch(addCollectionsToTemplate({ templateId: props.templateId, data: { ids: [collectionId] } }));
      setIsLoading(false);
    },
    [dispatch, props.templateId]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await dispatch(fetchCollections({}));
      setIsLoading(false);
    })();
  }, [dispatch]);
  useDebounce(
    () => {
      if (name !== undefined && !isLoading) {
        (async () => {
          setIsLoading(true);
          await dispatch(fetchCollections({ params: { name: name } }));
          setIsLoading(false);
        })();
      }
    },
    300,
    [dispatch, name]
  );

  return (
    <>
      <Input
        autoFocus
        allowClear
        placeholder={intl.formatMessage({ id: 'placeholder.SearchVideo' })}
        prefix={<SearchOutlined />}
        onChange={e => {
          setName(e.target.value);
        }}
        style={{ marginBottom: 10 }}
      />
      <div className={styles.content}>
        <LoaderWrapper loading={isLoading}>
          {collections
            .filter(c => !currentCollectionIds.includes(c.id) && !c.archived)
            .map(c => (
              <div key={c.id} className={tileStyles.listTile}>
                <Link to={`/content/collections/${c.id}/videos`} className={tileStyles.tileName}>
                  <Space>
                    <ProfileTwoTone />
                    {c.name}
                  </Space>
                </Link>
                <Space>
                  {c.archived && (
                    <Tag color={'orange'}>
                      <FormattedMessage id={'text.archived'} />
                    </Tag>
                  )}
                  <Button type={'primary'} size='small' onClick={() => handleAddButtonClick(c.id)}>
                    <FormattedMessage id={'action.Add'} />
                  </Button>
                </Space>
              </div>
            ))}
        </LoaderWrapper>
      </div>
    </>
  );
};

const SortableItem: React.FC<PropsWithChildren<{ id: string; key: string }>> = props => {
  const { children } = props;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  } as React.CSSProperties;
  return (
    <div style={style} ref={setNodeRef} {...attributes} {...listeners} className={tileStyles.listTile}>
      {children}
    </div>
  );
};

const ContentTemplateCollectionsTab: React.FC = () => {
  const { templateId } = useParams() as { templateId: string };
  const [addMode, setAddMode] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const template = useTemplateById(templateId) as ContentTemplate;
  const collections = useTemplateCollectionsSectionById(templateId);
  const dispatch: AppDispatch = useDispatch();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleRemoveButtonClick = useCallback(
    async (ids: string) => {
      setIsLoading(true);
      await dispatch(removeCollectionsFromTemplate({ templateId, data: { ids: [ids] } }));
      setIsLoading(false);
    },
    [dispatch, templateId]
  );

  const handleAddButtonClick = useCallback(() => {
    setAddMode(m => !m);
  }, []);

  const handleEditButtonClick = useCallback(() => {
    setEditMode(m => !m);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = collections.findIndex((i: Collection) => i.id === active.id);
        const newIndex = collections.findIndex((i: Collection) => i.id === over?.id);
        const result = arrayMove(collections, oldIndex, newIndex);
        setIsLoading(true);
        await dispatch(
          updateTemplateCollectionsOrder({
            templateId: template.id,
            data: { ids: result.map(r => r.id) },
          })
        );
        setIsLoading(false);
      }
    },
    [collections, dispatch, template.id]
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Коллекции</Typography.Title>
        <Space direction={'horizontal'}>
          {!editMode && (
            <Button type={addMode ? undefined : 'primary'} onClick={handleAddButtonClick}>
              {addMode ? <CloseOutlined /> : <PlusOutlined />}
            </Button>
          )}
          {!addMode && (
            <Button onClick={handleEditButtonClick}>{editMode ? <CloseOutlined /> : <EditOutlined />}</Button>
          )}
        </Space>
      </div>
      <div className={styles.content}>
        {addMode && <TemplateAddCollections templateId={templateId} currentCollections={collections} />}
        {!addMode && (
          <LoaderWrapper loading={isLoading}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={collections || []} strategy={verticalListSortingStrategy}>
                {collections?.map(c => (
                  <SortableItem key={c.id} id={c.id}>
                    <Link to={`/content/collections/${c.id}`} className={tileStyles.tileName}>
                      <Space>
                        <ProfileTwoTone />
                        {c.name}
                      </Space>
                    </Link>
                    <Space>
                      {!editMode && (
                        <>
                          <Tag color={'magenta'}>{c.category || '-'}</Tag>
                          <Divider type={'vertical'} />
                          {c.deviceTypes.length > 0 ? c.deviceTypes.map(t => <Tag key={t}>{t}</Tag>) : <Tag>All</Tag>}
                        </>
                      )}
                      {editMode && (
                        <ConfirmButton danger size='small' onClick={() => handleRemoveButtonClick(c.id)}>
                          <FormattedMessage id={'action.Remove'} />
                        </ConfirmButton>
                      )}
                    </Space>
                  </SortableItem>
                ))}
              </SortableContext>
            </DndContext>
          </LoaderWrapper>
        )}
      </div>
    </div>
  );
};
export default ContentTemplateCollectionsTab;
